import React, {useMemo} from 'react';
import {Accordion, Alert, Button, Col, Form, Row} from 'react-bootstrap';
import {toast} from 'react-toastify';
import * as yup from 'yup';
import {useFormik} from "formik";
import aiMentionsContentDefaults from "../../content/aiMentionsContentDefaults";
import {
    AI_MENTIONS_CONTENT_URL,
    AI_MENTIONS_LIMITS,
    AI_MENTIONS_NUMBER_PATTERN,
    AI_MENTIONS_STEP_COUNT,
    aiMentionsContentFreshUrl,
    aiMentionsDraftStore,
    parseAiMentionsContentValues
} from "../../hooks/useAiMentionsContent";
import useRevisionedContentAdmin, {useUnsavedChangesGuard} from "../../hooks/useRevisionedContentAdmin";
import {AiMentionsContentValues} from "../../models/AiMentionsContent";

// Shared with parseAiMentionsContentValues and the backend's pydantic models.
const LIMITS = AI_MENTIONS_LIMITS;

// Flat form shape: formik's nested error/touched types are painful, and the
// mapping to the API's nested body is trivial.
interface AiMentionsFormValues {
    title: string;
    subtitle: string;
    ctaPrimaryLabel: string;
    ctaSecondaryLabel: string;
    whatsappNumber: string;
    whatsappPrefill: string;
    whyNowTitle: string;
    whyNowBody: string;
    twoMinutesTitle: string;
    twoMinutesBody: string;
    twoMinutesCaption: string;
    step1Title: string;
    step1Body: string;
    step2Title: string;
    step2Body: string;
    step3Title: string;
    step3Body: string;
    stepsClosing: string;
    proofTitle: string;
    proofBody: string;
    finalCtaTitle: string;
    finalCtaSubtitle: string;
    finalCtaButtonLabel: string;
    finalCtaLinkText: string;
    chatDemoQuestion: string;
    chatDemoAnswer: string;
}

// The rail is exactly three steps wide - the row indices below are fixed, not a
// list the admin can grow.
const STEP_ROWS = Array.from({length: AI_MENTIONS_STEP_COUNT}, (_unused, index) => index + 1) as (1 | 2 | 3)[];
const stepField = (row: 1 | 2 | 3, part: 'Title' | 'Body') =>
    `step${row}${part}` as keyof AiMentionsFormValues;

const toFormValues = (content: AiMentionsContentValues): AiMentionsFormValues => {
    const step = (index: number) => content.steps[index] ?? {title: '', body: ''};

    return {
        title: content.hero.title,
        subtitle: content.hero.subtitle,
        ctaPrimaryLabel: content.hero.ctaPrimaryLabel,
        ctaSecondaryLabel: content.hero.ctaSecondaryLabel,
        whatsappNumber: content.whatsapp.number,
        whatsappPrefill: content.whatsapp.prefill,
        whyNowTitle: content.whyNow.title,
        whyNowBody: content.whyNow.body,
        twoMinutesTitle: content.twoMinutes.title,
        twoMinutesBody: content.twoMinutes.body,
        twoMinutesCaption: content.twoMinutes.caption,
        step1Title: step(0).title,
        step1Body: step(0).body,
        step2Title: step(1).title,
        step2Body: step(1).body,
        step3Title: step(2).title,
        step3Body: step(2).body,
        stepsClosing: content.stepsClosing,
        proofTitle: content.proof.title,
        proofBody: content.proof.body,
        finalCtaTitle: content.finalCta.title,
        finalCtaSubtitle: content.finalCta.subtitle,
        finalCtaButtonLabel: content.finalCta.buttonLabel,
        finalCtaLinkText: content.finalCta.linkText,
        chatDemoQuestion: content.chatDemo.question,
        chatDemoAnswer: content.chatDemo.answer,
    };
};

const toAiMentionsContent = (values: AiMentionsFormValues): AiMentionsContentValues => ({
    hero: {
        title: values.title.trim(),
        subtitle: values.subtitle.trim(),
        ctaPrimaryLabel: values.ctaPrimaryLabel.trim(),
        ctaSecondaryLabel: values.ctaSecondaryLabel.trim(),
    },
    whatsapp: {
        number: values.whatsappNumber.trim(),
        prefill: values.whatsappPrefill.trim(),
    },
    whyNow: {
        title: values.whyNowTitle.trim(),
        body: values.whyNowBody.trim(),
    },
    twoMinutes: {
        title: values.twoMinutesTitle.trim(),
        body: values.twoMinutesBody.trim(),
        caption: values.twoMinutesCaption.trim(),
    },
    steps: STEP_ROWS.map(row => ({
        title: (values[stepField(row, 'Title')] as string).trim(),
        body: (values[stepField(row, 'Body')] as string).trim(),
    })),
    stepsClosing: values.stepsClosing.trim(),
    proof: {
        title: values.proofTitle.trim(),
        body: values.proofBody.trim(),
    },
    finalCta: {
        title: values.finalCtaTitle.trim(),
        subtitle: values.finalCtaSubtitle.trim(),
        buttonLabel: values.finalCtaButtonLabel.trim(),
        linkText: values.finalCtaLinkText.trim(),
    },
    chatDemo: {
        question: values.chatDemoQuestion.trim(),
        answer: values.chatDemoAnswer.trim(),
    },
});

const requiredText = (limit: number, label: string) =>
    yup.string()
        .trim()
        .required(`נדרש ${label}`)
        .max(limit, `${label} מוגבל ל-${limit} תווים`);

const aiMentionsSchema = yup.object().shape({
    title: requiredText(LIMITS.title, 'כותרת'),
    subtitle: requiredText(LIMITS.subtitle, 'תת-כותרת'),
    ctaPrimaryLabel: requiredText(LIMITS.ctaPrimaryLabel, 'טקסט לכפתור הראשי'),
    ctaSecondaryLabel: requiredText(LIMITS.ctaSecondaryLabel, 'טקסט לכפתור המשני'),
    whatsappNumber: yup.string()
        .trim()
        .required('נדרש מספר וואטסאפ')
        .matches(AI_MENTIONS_NUMBER_PATTERN, 'מספר הוואטסאפ חייב להיות בפורמט 9725XXXXXXXX (ללא + וללא רווחים)'),
    whatsappPrefill: requiredText(LIMITS.prefill, 'הודעת הפתיחה'),
    whyNowTitle: requiredText(LIMITS.whyNowTitle, 'כותרת'),
    whyNowBody: requiredText(LIMITS.whyNowBody, 'טקסט'),
    twoMinutesTitle: requiredText(LIMITS.twoMinutesTitle, 'כותרת'),
    twoMinutesBody: requiredText(LIMITS.twoMinutesBody, 'טקסט'),
    twoMinutesCaption: requiredText(LIMITS.twoMinutesCaption, 'כיתוב'),
    step1Title: requiredText(LIMITS.stepTitle, 'כותרת שלב'),
    step1Body: requiredText(LIMITS.stepBody, 'טקסט שלב'),
    step2Title: requiredText(LIMITS.stepTitle, 'כותרת שלב'),
    step2Body: requiredText(LIMITS.stepBody, 'טקסט שלב'),
    step3Title: requiredText(LIMITS.stepTitle, 'כותרת שלב'),
    step3Body: requiredText(LIMITS.stepBody, 'טקסט שלב'),
    stepsClosing: requiredText(LIMITS.stepsClosing, 'משפט סיום'),
    proofTitle: requiredText(LIMITS.proofTitle, 'כותרת'),
    proofBody: requiredText(LIMITS.proofBody, 'טקסט'),
    finalCtaTitle: requiredText(LIMITS.finalCtaTitle, 'כותרת'),
    finalCtaSubtitle: requiredText(LIMITS.finalCtaSubtitle, 'תת-כותרת'),
    finalCtaButtonLabel: requiredText(LIMITS.finalCtaButtonLabel, 'טקסט לכפתור'),
    finalCtaLinkText: requiredText(LIMITS.finalCtaLinkText, 'טקסט הקישור'),
    chatDemoQuestion: requiredText(LIMITS.chatDemoQuestion, 'שאלה'),
    // the emphasis lives inside this string now - the markers count against the limit
    chatDemoAnswer: requiredText(LIMITS.chatDemoAnswer, 'תשובה'),
});

const AdminAiMentionsContent: React.FC = () => {
    const admin = useRevisionedContentAdmin<AiMentionsContentValues>({
        url: AI_MENTIONS_CONTENT_URL,
        freshUrl: aiMentionsContentFreshUrl,
        defaults: aiMentionsContentDefaults,
        parseValues: parseAiMentionsContentValues,
        draftStore: aiMentionsDraftStore,
        previewUrl: token => `/#/ai-mentions?preview=${token}`,
        messages: {
            loadError: 'בעיה בטעינת תוכן דף AI Mentions',
            saveError: 'בעיה בשמירת תוכן דף AI Mentions',
            invalidResponse: 'התקבלה תשובה לא תקינה מהשרת, התוכן לא עודכן במסך',
            conflict: 'התוכן עודכן במקביל — נטען מחדש',
            saveSuccess: 'התוכן נשמר ופורסם בהצלחה',
            previewError: 'יש לתקן את השגיאות בטופס לפני תצוגה מקדימה',
            previewWriteError: 'בעיה ביצירת התצוגה המקדימה',
            stashInfo: key => `הגרסה שניסית לשמור נשמרה מקומית (${key})`,
        },
    });

    const formValues = useMemo(() => toFormValues(admin.values), [admin.values]);

    const formik = useFormik<AiMentionsFormValues>({
        initialValues: formValues,
        validationSchema: aiMentionsSchema,
        onSubmit: values => admin.save(toAiMentionsContent(values)),
        validateOnBlur: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    useUnsavedChangesGuard(formik.dirty || admin.isSaving);

    const handlePreview = async () => {
        // A preview tab renders whatever it is handed, so an invalid draft must
        // never be written: validate exactly like a save would.
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched(
                Object.keys(formik.values).reduce(
                    (touched, key) => ({...touched, [key]: true}),
                    {} as Record<string, boolean>
                )
            );
            toast.error('יש לתקן את השגיאות בטופס לפני תצוגה מקדימה');
            return;
        }

        admin.openPreview(toAiMentionsContent(formik.values));
    };

    const restoreStashed = () => {
        if (!admin.stashed) {
            return;
        }
        formik.setValues(toFormValues(admin.stashed.values));
        admin.clearStash();
    };

    const field = (name: keyof AiMentionsFormValues, label: string, limit?: number, rows?: number) => {
        const value = formik.values[name];
        const error = formik.touched[name] ? formik.errors[name] : undefined;
        // initialValues is the saved copy (reset on load and after every save),
        // so a difference means "not published yet"
        const changed = value !== formik.initialValues[name];

        return (
            <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-1">
                        {label}
                        {changed && <span className="content-changed-chip">שונה</span>}
                    </Form.Label>
                    {limit !== undefined && (
                        <span className={`text-small ${value.length > limit ? 'text-danger' : 'text-gray'}`}>
                            {value.length}/{limit}
                        </span>
                    )}
                </div>
                {rows ? (
                    <Form.Control
                        as="textarea"
                        rows={rows}
                        name={name}
                        value={value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={!!error}
                        className={changed ? 'content-changed' : undefined}
                    />
                ) : (
                    <Form.Control
                        type="text"
                        name={name}
                        value={value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={!!error}
                        className={changed ? 'content-changed' : undefined}
                    />
                )}
                <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            </Form.Group>
        );
    };

    return (
        <Row className="justify-content-center py-md-5">
            <Col md={12}>
                <h1 className="mb-4">תוכן AI Mentions</h1>
            </Col>
            <Col md={10}>
                {admin.isLoading ? (
                    <div>טוען...</div>
                ) : (
                    <Form noValidate onSubmit={formik.handleSubmit}>
                        {admin.stashed && (
                            <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                                <span>הגרסה שניסית לשמור נשמרה מקומית ולא אבדה.</span>
                                <span>
                                    <Button variant="outline-dark" size="sm" className="me-2" type="button"
                                            onClick={restoreStashed}>
                                        שחזור העריכה שלי
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" type="button"
                                            onClick={admin.clearStash}>
                                        התעלמות
                                    </Button>
                                </span>
                            </Alert>
                        )}

                        {/* Every section open: the whole page is one document and
                            an admin editing it should see all of it at once. */}
                        <Accordion
                            defaultActiveKey={['hero', 'chatDemo', 'whatsapp', 'whyNow', 'twoMinutes', 'steps',
                                'proof', 'finalCta']}
                            alwaysOpen
                        >
                            <Accordion.Item eventKey="hero">
                                <Accordion.Header>הירו</Accordion.Header>
                                <Accordion.Body>
                                    {field('title', 'כותרת ראשית', LIMITS.title, 2)}
                                    <div className="text-small text-gray mb-3">
                                        כל שורה אחרי הראשונה מוצגת בצבע ההדגשה (מעבר שורה = Enter).
                                    </div>
                                    {field('subtitle', 'תת-כותרת', LIMITS.subtitle, 4)}
                                    {field('ctaPrimaryLabel', 'כפתור ראשי (וואטסאפ)', LIMITS.ctaPrimaryLabel)}
                                    {field('ctaSecondaryLabel', 'כפתור משני (מעבר לחבילות)', LIMITS.ctaSecondaryLabel)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="chatDemo">
                                <Accordion.Header>הדגמת השיחה</Accordion.Header>
                                <Accordion.Body>
                                    <div className="text-small text-gray mb-3">
                                        השאלה והתשובה מוקלדות בלולאה בתוך ההירו.
                                        הדגשה: עוטפים טקסט בכוכביות, למשל *העסק שלכם*
                                    </div>
                                    {field('chatDemoQuestion', 'שאלה', LIMITS.chatDemoQuestion, 2)}
                                    {field('chatDemoAnswer', 'תשובה (הדגשה: *טקסט*)', LIMITS.chatDemoAnswer, 4)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="whatsapp">
                                <Accordion.Header>וואטסאפ</Accordion.Header>
                                <Accordion.Body>
                                    <div className="text-small text-gray mb-3">
                                        שני הכפתורים בדף (הירו והקריאה לפעולה בסוף) פותחים את אותה שיחה עם אותה הודעה.
                                    </div>
                                    {field('whatsappNumber', 'מספר וואטסאפ (9725XXXXXXXX)')}
                                    {field('whatsappPrefill', 'הודעת פתיחה', LIMITS.prefill, 4)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="whyNow">
                                <Accordion.Header>למה עכשיו</Accordion.Header>
                                <Accordion.Body>
                                    {field('whyNowTitle', 'כותרת', LIMITS.whyNowTitle, 2)}
                                    {field('whyNowBody', 'טקסט', LIMITS.whyNowBody, 6)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="twoMinutes">
                                <Accordion.Header>AI Mentions בשתי דקות</Accordion.Header>
                                <Accordion.Body>
                                    {field('twoMinutesTitle', 'כותרת', LIMITS.twoMinutesTitle, 2)}
                                    {field('twoMinutesBody', 'טקסט', LIMITS.twoMinutesBody, 6)}
                                    {field('twoMinutesCaption', 'כיתוב מתחת לשתי השכבות',
                                        LIMITS.twoMinutesCaption, 3)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="steps">
                                <Accordion.Header>איך זה עובד</Accordion.Header>
                                <Accordion.Body>
                                    <div className="text-small text-gray mb-3">
                                        שלושה שלבים, לא יותר ולא פחות — המספור בדף נגזר מהסדר כאן.
                                    </div>
                                    {STEP_ROWS.map(row => (
                                        <Row key={row}>
                                            <Col md={5}>
                                                {field(stepField(row, 'Title'), `שלב ${row} — כותרת`,
                                                    LIMITS.stepTitle)}
                                            </Col>
                                            <Col md={7}>
                                                {field(stepField(row, 'Body'), `שלב ${row} — טקסט`,
                                                    LIMITS.stepBody, 2)}
                                            </Col>
                                        </Row>
                                    ))}
                                    {field('stepsClosing', 'משפט סיום', LIMITS.stepsClosing, 2)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="proof">
                                <Accordion.Header>למה לסמוך עלינו</Accordion.Header>
                                <Accordion.Body>
                                    {field('proofTitle', 'כותרת', LIMITS.proofTitle, 2)}
                                    {field('proofBody', 'טקסט', LIMITS.proofBody, 6)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="finalCta">
                                <Accordion.Header>קריאה לפעולה בסוף הדף</Accordion.Header>
                                <Accordion.Body>
                                    {field('finalCtaTitle', 'כותרת', LIMITS.finalCtaTitle, 2)}
                                    {field('finalCtaSubtitle', 'תת-כותרת', LIMITS.finalCtaSubtitle, 2)}
                                    {field('finalCtaButtonLabel', 'טקסט לכפתור (וואטסאפ)',
                                        LIMITS.finalCtaButtonLabel)}
                                    {field('finalCtaLinkText', 'טקסט הקישור לחבילות', LIMITS.finalCtaLinkText, 2)}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <div className="d-flex gap-2 mt-4">
                            <Button variant="primary" type="submit" disabled={admin.isSaving}>
                                שמירה ופרסום
                            </Button>
                            <Button variant="outline-secondary" type="button" onClick={handlePreview}>
                                תצוגה מקדימה
                            </Button>
                        </div>
                    </Form>
                )}
            </Col>
        </Row>
    );
};

export default AdminAiMentionsContent;
